import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import {
    Button,
    Checkbox,
    Field,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    SearchableDropdown,
} from "modules/common";
import React, { useEffect, useState } from "react";
import { PhytoviewEntry, PhytoviewRequest } from "services/phytoview";
import { IPhytoViewModalProps } from "views";
import {
    UseCreatePhytoViewEntry,
    UseEditPhytoViewEntry,
    useGetKategorijaAsItems,
    useGetOnePhytoView,
} from "../../hooks/phytoViewHook";

const emptyPhytoView: PhytoviewRequest = {
    name: "",
    description: "",
    active: false,
};

const validateFenofazePodkategorijeRecord = (
    phytoview: PhytoviewRequest,
    kategorija: { label: string; id: number | null }
): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!phytoview.name.trim()) {
        errors.name = "Naziv je obavezan";
    }
    if (kategorija?.id == null) {
        errors.kategorija = "Kategorija je obavezna";
    }
    return errors;
};

const PhytoViewFenofazePodkategorijeModal: React.FC<IPhytoViewModalProps> = ({
    title,
    isOpen,
    onClose,
    isPost,
    editItemId,
}) => {
    const endpoint = "PredmetOpazanjaFenofaze";
    const [phytoview, setPhytoview] = useState<PhytoviewRequest>(emptyPhytoView);
    const [, setIsEdit] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { data: dataOne } = useGetOnePhytoView(endpoint, editItemId);
    const create = UseCreatePhytoViewEntry(endpoint);
    const edit = UseEditPhytoViewEntry(endpoint, editItemId);
    const inputReset = () => {
        setPhytoview({ ...emptyPhytoView });
        setKategorija({ label: "", id: null });
        setErrors({});
    };
    const kategorijaOptions = useGetKategorijaAsItems();
    const [kategorija, setKategorija] = useState<{ label: string; id: number | null }>({
        label: "",
        id: null,
    });
    useEffect(() => {
        if (!isPost && isOpen && dataOne) {
            setPhytoview(prev => ({
                ...prev,
                active: dataOne.active ?? true,
                name: dataOne.name ?? "",
                description: dataOne.description ?? "",
            }));
            setKategorija({
                label: dataOne.kategorija?.name ?? "",
                id: dataOne.kategorija?.id ?? null,
            });
        } else {
            inputReset();
        }
    }, [isPost, isOpen, dataOne]);

    if (!isOpen) return null;
    const onCancel = () => {
        inputReset();
        setIsEdit(false);
        setIsFormChanged(false);
        onClose();
    };
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setPhytoview(prev => ({ ...prev, active: checked }));
        setIsFormChanged(true);
    };

    const isValid = (property: string) => {
        return !errors[property];
    };

    const getErrorMsg = (property: string) => {
        return errors[property] ?? "";
    };

    const onSave = async () => {
        const newErrors = validateFenofazePodkategorijeRecord(phytoview, kategorija);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        setErrors({});

        const payload: PhytoviewEntry = {
            name: phytoview.name,
            description: phytoview.description,
            active: phytoview.active,
            parentId: kategorija.id,
            jls: [],
            naziv: kategorija.label,
            created: "",
            createdBy: "",
            lastUpdated: "",
            lastUpdatedBy: "",
            id: 0,
            kategorijaId: kategorija.id,
        };
        const mutation = isPost ? create.mutateAsync : edit.mutateAsync;
        await mutation(payload);
        inputReset();
        setIsFormChanged(false);
        onClose();
    };

    return (
        <Modal
            isActive={isOpen}
            className="phytoview-details-modal"
            onClose={onClose}
            modalHeader={
                <ModalHeader onClose={onClose} title={title} className="phytoview-modal-header" />
            }
            modalFooter={
                <ModalFooter>
                    <div className="phytoview-footer">
                        <Button
                            type="button"
                            size="small"
                            variant="primary-ghost"
                            disabled={false}
                            onClick={onCancel}
                        >
                            <span>ODUSTANI</span>
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            variant="primary"
                            disabled={!isFormChanged}
                            onClick={onSave}
                        >
                            <span>SPREMI</span>
                        </Button>
                    </div>
                </ModalFooter>
            }
        >
            <div className="phytoview-details">
                <div className="modal-section">
                    <Field name="NAZIV" errorMessage={getErrorMsg("name")}>
                        <Input
                            size="small"
                            name="name_input_filed"
                            id="name_id"
                            type="text"
                            isValid={isValid("name")}
                            value={phytoview?.name}
                            onChange={e => {
                                setPhytoview({ ...phytoview, name: e.target.value });
                                setIsFormChanged(true);
                            }}
                        />
                    </Field>
                </div>

                <div className="modal-section">
                    <Field name="OPIS" errorMessage={getErrorMsg("description")}>
                        <Input
                            size="large"
                            name="desc_input_filed"
                            id="desc_id"
                            type="text"
                            isValid={isValid("description")}
                            value={phytoview.description}
                            onChange={e => {
                                setPhytoview({ ...phytoview, description: e.target.value });
                                setIsFormChanged(true);
                            }}
                        />
                    </Field>
                </div>

                <div className="modal-section">
                    <Field name="AKTIVAN" errorMessage={getErrorMsg("active")}>
                        <Checkbox
                            isChecked={phytoview.active}
                            type="checkbox"
                            htmlFor="example-checkbox"
                            text="Da"
                            onChange={handleCheckboxChange}
                            className="custom-checkbox"
                            value="checkbox-value"
                            isReversed={true}
                        />
                    </Field>
                </div>

                <div className="modal-section">
                    <Field name="KATEGORIJA" errorMessage={getErrorMsg("kategorija")}>
                        <SearchableDropdown
                            items={kategorijaOptions}
                            value={kategorijaOptions}
                            noOptionsMessage={() => "Nema pronađenih kategorija"}
                            labelKey="label"
                            placeholder="Pretraživanje"
                            customIcons={{
                                searchIcon: <SearchIcon width={14} height={14} />,
                            }}
                            isValid={isValid("kategorija")}
                            onSelect={() => {}}
                            disabled={true}
                        />
                    </Field>
                </div>
            </div>
        </Modal>
    );
};

export default PhytoViewFenofazePodkategorijeModal;
